import {Box, Button, IconButton, Typography, Select, MenuItem, FormControl} from "@mui/material";
import {ReactZoomPanPinchRef, TransformComponent, TransformWrapper,} from "react-zoom-pan-pinch";
import {Document, Page} from "react-pdf";
import {Circle, Loader2, RotateCw} from "lucide-react";
import {useCallback, useContext, useEffect, useMemo, useState} from "react"; // Configure pdf.js worker
import {PdfWorker} from "@/plugin/pdfjs-plugin"; // Configure pdf.js worker
import {nonNullable} from "@/utils/types";
import {HighlightedField, SetHighlightedField} from "@/hooks/use-highlight-field";
import {BoundingRegionOutput} from "@azure-rest/ai-document-intelligence";
import {useUpdateRotationMutation} from "@/graphql/graphql";
import {IPageAnchor} from "@/types/document-ai-type.ts";

function chunkedArr<T>(arrary: T[], size: number): T[][] {
  return arrary.flatMap((_, i) =>
    i % size ? [] : [arrary.slice(i, i + size)],
  );
}

export function PdfViewer() {
  const highlighted = useContext(HighlightedField);
  const setHighlighted = useContext(SetHighlightedField)
  
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(1);
  const [_isLoading, setIsLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);
  const [transformApi, setTransformApi] = useState<ReactZoomPanPinchRef | null>(
    null,
  );
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [handRotations, setHandRotations] = useState<Map<string, number>>(new Map());
  
  const currentHandRotation = useMemo(() => {
    if (!highlighted.file?.id) return 0;
    return handRotations.get(highlighted.file.id) ?? highlighted.file?.handRotation ?? 0;
  }, [handRotations, highlighted.file]);
  
  const updateHandRotation = useUpdateRotationMutation()
  
  const onDocumentLoadSuccess = useCallback(
    ({numPages}: { numPages: number }) => {
      setRotation(0)
      setNumPages(numPages);
      setPageNumber(1); // Reset to first page
      setIsLoading(false);
    },
    [],
  );
  
  const fileUrl = useMemo(() => {
    return highlighted.file?.url
  }, [highlighted.file?.url])
  
  const getAzurePageInfo = useCallback(
    (pageNumber: number) => {
      return highlighted?.file?.azureAnalyzeResult?.analyzeResult?.pages.find(
        (page) => page.pageNumber === pageNumber,
      );
    },
    [highlighted?.file?.azureAnalyzeResult?.analyzeResult?.pages],
  );
  
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  
  useEffect(() => {
    if (transformApi?.instance.contentComponent) {
      const updateDimensions = () => {
        setDimensions({
          width: transformApi.instance.contentComponent?.clientWidth || 0,
          height: transformApi.instance.contentComponent?.clientHeight || 0,
        });
      };
      
      updateDimensions();
      
      const resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(transformApi.instance.contentComponent);
      
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [transformApi, highlighted.field]);
  
  const viewportWidthWrapper = useMemo(() => {
    return transformApi?.instance.wrapperComponent?.clientWidth;
  }, [transformApi?.instance.wrapperComponent?.clientWidth]);
  const viewportHeightWrapper = useMemo(() => {
    return transformApi?.instance.wrapperComponent?.clientHeight;
  }, [transformApi?.instance.wrapperComponent?.clientHeight]);
  
  const renderHighlights = useMemo(() => {
    if (!transformApi) return null;
    
    if (dimensions.width < 100 || dimensions.height < 100) return null;
    
    if (!highlighted.field) return null;
    
    if (!highlighted.file) return null;
    
    let x = 0;
    let y = 0;
    let width = 0;
    let height = 0;
    
    const {
      pageAnchor,
      boundingRegions,
    }: {
      pageAnchor?: IPageAnchor;
      boundingRegions?: BoundingRegionOutput[];
    } = highlighted.field;
    
    setRotation(0)
    if (pageAnchor) {
      setPageNumber(Number(pageAnchor.pageRefs?.[0]?.page ?? 0) + 1);
      
      const boundingPoly =
        pageAnchor.pageRefs?.[0]?.boundingPoly?.normalizedVertices;
      if (!boundingPoly?.length) return null;
      
      const xCoords = boundingPoly.map((xy) => xy.x).filter(nonNullable);
      const yCoords = boundingPoly.map((xy) => xy.y).filter(nonNullable);
      
      const left = Math.min(...xCoords);
      const right = Math.max(...xCoords);
      const top = Math.min(...yCoords);
      const bottom = Math.max(...yCoords);
      
      const pageData = highlighted.file.pageData.find((data: any) => data.pageNumber === Number(pageAnchor.pageRefs?.[0]?.page ?? 0) + 1);
      
      if (pageData?.rotation) {
        setRotation(Math.floor(pageData.rotation / 90) * 90);
      }
      
      x = left * dimensions.width;
      y = top * dimensions.height;
      width = (right - left) * dimensions.width * scale;
      height = (bottom - top) * dimensions.height * scale;
    } else if (boundingRegions) {
      setPageNumber(boundingRegions[0].pageNumber);
      
      // Get page dimensions from result
      const page = getAzurePageInfo(boundingRegions[0].pageNumber);
      
      if (!page?.width || !page?.height) return null;
      
      const polygon = boundingRegions.map((region) => region.polygon).flat();
      if (!polygon?.length) return null;
      
      const xyArray = chunkedArr(polygon, 2);
      const xCoords = xyArray.map((xy) => xy[0]);
      const yCoords = xyArray.map((xy) => xy[1]);
      
      // Calculate bounding box from polygon points
      const left = Math.min(...xCoords);
      const right = Math.max(...xCoords);
      const top = Math.min(...yCoords);
      const bottom = Math.max(...yCoords);
      
      x = (left / page.width) * dimensions.width!;
      y = (top / page.height) * dimensions.height!;
      width = ((right - left) / page.width) * dimensions.width! * scale;
      height = ((bottom - top) / page.height) * dimensions.height! * scale;
    }
    if (width === 0 || height === 0) return null;
    
    const padding = 6;
    
    setTimeout(() => {
      transformApi.zoomToElement("highlight", scale, 100);
    }, 100);
    return (
      <Box
        sx={{
          position: "absolute",
          border: "4px solid",
          borderColor: "red",
        }}
        id={`highlight`}
        key={`highlight`}
        style={{
          top: `${y - padding}px`,
          width: `${width + padding * 4}px`,
          height: `${height + padding * 4}px`,
          left: `${x - padding}px`,
          transform: `scale(${1 / (scale || 1)})`,
          transformOrigin: "top left",
          zIndex: 50,
        }}
      />
    );
  }, [
    dimensions.height,
    dimensions.width,
    getAzurePageInfo,
    highlighted.field,
    highlighted.file,
    scale,
    transformApi,
  ]);
  const handleLoadError = useCallback((error: Error) => {
    console.error(error);
    setIsLoading(false);
    setError(error.message);
  }, []);
  
  const reactPdfOption = useMemo(() => ({
    worker: PdfWorker,
  }), [])
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: 1,
          zIndex: 10,
          borderRadius: 2,
        }}
      >
        <Button
          color="secondary"
          variant={"contained"}
          size="small"
          onClick={() => {
            setHighlighted((prev) => {
              return {
                ...prev,
                field: null
              }
            })
            setPageNumber((prev) => Math.max(1, prev - 1));
            
          }}
          disabled={pageNumber <= 1}
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {numPages}
        </span>
        <Button
          color="secondary"
          variant={"contained"}
          size="small"
          onClick={() => {
            setHighlighted((prev) => {
              return {
                ...prev,
                field: null
              }
            })
            setPageNumber((prev) => Math.min(numPages, prev + 1));
          }}
          disabled={pageNumber >= numPages}
        >
          Next
        </Button>
      </Box>
      <Typography sx={{mb: 1}}>
        ハイライトが正しくない場合、<RotateCw style={{height: "1rem"}}/>
        ボタンから調整してください</Typography>
      <Box
        sx={{
          position: "relative",
          overflow: 'hidden',
          width: "100%",
          height: "600px",
        }}
      >
        {
          highlighted.file?.url ?
            <TransformWrapper
              maxScale={10}
              minScale={0.3}
              initialScale={1}
              centerZoomedOut={true}
              onInit={(reactZoomPanPinchRef: ReactZoomPanPinchRef) => {
                setTransformApi(reactZoomPanPinchRef);
              }}
            >
              {({resetTransform}) => (
                <>
                  <Box
                    sx={{
                      zIndex: 10,
                      position: "absolute",
                      top: 2,
                      right: 2,
                      gap: 2,
                    }}
                  >
                    <FormControl size="small" sx={{minWidth: 120, backgroundColor: 'white', borderRadius: 1}}>
                      <Select size="small"
                              value={scale}
                              onChange={(e) => {
                                const newScale = Number(e.target.value);
                                setScale(newScale);
                                transformApi?.setTransform(0, 0, newScale);
                              }}
                      >
                        <MenuItem value={1}>1倍</MenuItem>
                        <MenuItem value={1.2}>1.2倍</MenuItem>
                        <MenuItem value={1.5}>1.5倍</MenuItem>
                        <MenuItem value={2}>2倍</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={() => resetTransform()}
                    >
                      <Circle className="h-4 w-4"/>
                    </IconButton>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={async () => {
                        if (!highlighted.file?.id) return;
                        const newRotation = (currentHandRotation ?? 0) + 90;
                        setHandRotations(prev => {
                          const next = new Map(prev);
                          next.set(highlighted.file!.id, newRotation);
                          return next;
                        });
                        if (highlighted.file)
                          await updateHandRotation.mutateAsync(
                            {
                              id: highlighted.file?.id,
                              rotation: newRotation
                            },
                          ).then((result) => {
                            setHighlighted((prev) => {
                              return {
                                ...prev,
                                file: result.updateRotation
                              }
                            })
                          })
                      }}
                    >
                      <RotateCw className="h-4 w-4"/>
                    </IconButton>
                  </Box>
                  <TransformComponent
                    wrapperStyle={{
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <div className="relative">
                      {highlighted?.file?.mimeType === "application/pdf" ? (
                        <Document
                          rotate={currentHandRotation ?? rotation}
                          options={reactPdfOption}
                          file={fileUrl}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={handleLoadError}
                          loading={
                            <div className="flex items-center justify-center h-full">
                              <Loader2 className="h-8 w-8 animate-spin"/>
                            </div>
                          }
                          error={
                            <div className="flex items-center justify-center h-full text-destructive">
                              Failed to load PDF
                            </div>
                          }
                        >
                          <Page
                            
                            height={viewportHeightWrapper}
                            width={viewportWidthWrapper}
                            pageNumber={pageNumber}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={
                              <div className="flex items-center justify-center h-full">
                                <Loader2 className="h-8 w-8 animate-spin"/>
                              </div>
                            }
                          />
                          {renderHighlights}
                        </Document>
                      ) : (
                        <>
                          <img
                            src={highlighted?.file?.url ?? undefined}
                            alt="Document preview"
                            style={{
                              objectFit: "contain",
                              width: "100%",
                              height: "100%",
                            }}
                            onLoad={() => {
                              setIsLoading(false);
                            }}
                            onError={() =>
                              handleLoadError(new Error("Failed to load image"))
                            }
                          />
                          {renderHighlights}
                        </>
                      )}
                    </div>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper> : <Typography sx={{whiteSpace: "pre-wrap"}}>
              {highlighted.file?.text}
            </Typography>
        }
      </Box>
    </>
  );
}
